// T033/T051 — A FICHA DE UM LAYOUT DO CATÁLOGO: tudo que se sabe sobre ele, numa página.
//
// Antes, a resposta a "esse layout serve para essa unidade?" estava espalhada:
// as limitações no catálogo, as pendências na cabeça de quem escreveu o
// adaptador, a prova num terminal, o documento num PDF. A ficha junta tudo, na
// ordem em que a pergunta é feita:
//
//  1. DÁ PARA ATRIBUIR? — cabeçalho: a situação e o motivo quando não dá.
//  2. O QUE FALTA PARA PUBLICAR? — pendências, só em rascunho.
//  3. FOI PROVADO? — a última prova por ambiente, e o que o catálogo DECLARA
//     confrontado com o que o banco deste ambiente REGISTRA. Quando divergem,
//     a linha de alerta diz — é a única defesa contra o "está verde" de memória.
//  4. QUE FORMA ELE ACEITA? — os cenários da matriz; o nível 2 em cinza,
//     porque "não aceito, com motivo" é informação, não erro.
//  5. O QUE NÃO CARREGA, O QUE DERIVA, QUEM USA, O DOCUMENTO E O EXEMPLO.
//
// 404 é um estado da tela, não uma exceção: o código vem da URL, e um link
// velho ou um layout de teste (invisível fora do vitest) cai aqui.

import { useMemo, type ReactNode } from "react";
import { useMutation, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import {
  Button,
  Card,
  DataTable,
  EstadoErro,
  EstadoVazio,
  Pill,
  Skeleton,
  fmtDataISO,
  type Column,
} from "@atlas/ui/web";
import { ApiHttpError, type AtlasClient } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { abrirDocumentoDeLayout } from "../../lib/layout-documento";
import { LayoutChip } from "../../components/LayoutChip";
import { Markdown } from "../../components/Markdown";

// Os tipos da 008 saem do próprio cliente: assim a tela acompanha o SDK sem
// depender de o índice do pacote reexportá-los um a um.
type LayoutsApi = AtlasClient["admin"]["layouts"];
type FichaLayout = Awaited<ReturnType<LayoutsApi["ficha"]>>;
type MatrizCenarios = Awaited<ReturnType<LayoutsApi["matriz"]>>;
type LayoutProva = FichaLayout["provas"][number];
type AmbienteProva = keyof FichaLayout["layout"]["ultimaProva"];
type ResumoProva = NonNullable<FichaLayout["layout"]["ultimaProva"][AmbienteProva]>;
type UnidadeQueUsa = FichaLayout["unidades"][number];

// ============================================================ helpers

/**
 * Cartão com título — o padrão desta área (ver `layouts-unidade.tsx`).
 * `carregando` marca o cartão com `aria-busy`: o leitor de tela sabe que o
 * esqueleto não é o conteúdo, e o teste sabe qual cartão esperar.
 */
function Secao({
  titulo,
  acoes,
  carregando,
  children,
}: {
  titulo: string;
  acoes?: ReactNode;
  carregando?: boolean;
  children: ReactNode;
}) {
  return (
    <Card aria-busy={carregando ? "true" : undefined}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 10,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 16 }}>{titulo}</h2>
        {acoes}
      </div>
      {children}
    </Card>
  );
}

const Travessao = () => <span style={{ color: "var(--text-dim)" }}>—</span>;

/** O status HTTP de um erro do SDK (`ApiHttpError`); `null` quando não é de HTTP. */
function statusDoErro(e: unknown): number | null {
  if (e instanceof ApiHttpError) return e.status;
  const s = (e as { status?: unknown } | null)?.status;
  return typeof s === "number" ? s : null;
}

function mensagemDoErro(e: unknown): string {
  return e instanceof Error && e.message ? e.message : "erro desconhecido";
}

/** Data e hora locais de um instante ISO. */
const fmtDataHora = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
/** Só a data local de um instante ISO — para a pílula, que não tem espaço para a hora. */
const fmtData = (iso: string) => new Date(iso).toLocaleDateString("pt-BR");
/** `desde` chega como competência (AAAAMM) ou como data; cada um se mostra do seu jeito. */
const fmtDesde = (s: string) => (/^\d{6}$/.test(s) ? s : fmtDataISO(s));

/**
 * Dispara o download de um blob com nome de arquivo.
 *
 * A rota do exemplo exige Authorization, então `<a href download>` direto daria
 * 401 em silêncio — a mesma razão de `abrirDocumentoDeLayout`. A diferença é o
 * fim: aqui o resultado é salvar um zip, não abrir uma aba.
 */
function baixarBlob(blob: Blob, nome: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revogar na hora cancela o download em alguns navegadores.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

const AMBIENTES: { chave: AmbienteProva; rotulo: string }[] = [
  { chave: "local", rotulo: "local" },
  { chave: "homolog", rotulo: "homolog" },
  { chave: "producao", rotulo: "produção" },
];
const ROTULO_AMBIENTE = Object.fromEntries(AMBIENTES.map((a) => [a.chave, a.rotulo])) as Record<
  AmbienteProva,
  string
>;

// ============================================================ tela

export function AdminLayoutsFicha() {
  const { codigo = "" } = useParams<{ codigo: string }>();

  const ficha = useQuery({
    queryKey: ["admin", "layouts", "ficha", codigo],
    queryFn: () => atlas.admin.layouts.ficha(codigo),
    // 404 é resposta, não falha passageira: retentar só atrasaria o "não encontrado".
    retry: false,
  });
  const matriz = useQuery({
    queryKey: ["admin", "layouts", "matriz"],
    queryFn: () => atlas.admin.layouts.matriz(),
  });

  const dados = ficha.data;
  const naoEncontrado = statusDoErro(ficha.error) === 404;

  return (
    // `minmax(0, 1fr)`: sem o mínimo em zero, a coluna implícita do grid cresce
    // até a tabela mais larga (as execuções da prova, com seis colunas) e a
    // PÁGINA passa a rolar de lado a 1024 px. Com ele, quem rola é o quadro da
    // tabela (T081, FR-038).
    <div style={{ display: "grid", gap: 16, gridTemplateColumns: "minmax(0, 1fr)" }}>
      <Cabecalho codigo={codigo} dados={dados} carregando={ficha.isLoading} />

      {naoEncontrado ? (
        <div style={{ display: "grid", gap: 12, justifyItems: "start" }}>
          <EstadoErro mensagem="Layout não encontrado." />
          <Link to="/averbadora/layouts" style={{ fontSize: 13, color: "var(--accent)" }}>
            ← Hub
          </Link>
        </div>
      ) : ficha.isError ? (
        <EstadoErro
          mensagem={`Não foi possível carregar a ficha do layout: ${mensagemDoErro(ficha.error)}`}
          onTentarDeNovo={() => void ficha.refetch()}
        />
      ) : !dados ? (
        <Esqueleto />
      ) : (
        <>
          {dados.layout.situacao === "rascunho" ? <PendenciasCard pendencias={dados.pendencias} /> : null}
          <ProvaCard ficha={dados} />
          <CenariosCard cenarios={dados.layout.cenarios} matriz={matriz} />
          <LimitacoesCard limitacoes={dados.layout.limitacoes} />
          <CamposDerivadosCard campos={dados.layout.camposDerivados} />
          <UnidadesCard unidades={dados.unidades} />
          <DocumentacaoCard codigo={codigo} />
          <ExemploCard codigo={codigo} exemplo={dados.exemplo} />
        </>
      )}
    </div>
  );
}

// ============================================================ cabeçalho

/**
 * Renderizado ANTES do dado: o link de volta e o `<h1>` já existem (com o
 * código da URL como nome provisório), e o que falta vira esqueleto com a
 * altura do que vai ocupar o lugar — o cabeçalho não pula quando a ficha chega.
 */
function Cabecalho({
  codigo,
  dados,
  carregando,
}: {
  codigo: string;
  dados?: FichaLayout;
  carregando: boolean;
}) {
  const layout = dados?.layout;
  return (
    <header style={{ display: "grid", gap: 6, gridTemplateColumns: "minmax(0, 1fr)" }}>
      <Link
        to="/averbadora/layouts"
        style={{ fontSize: 13, color: "var(--text-muted)", textDecoration: "none", justifySelf: "start" }}
      >
        ← Hub
      </Link>
      <h1 style={{ margin: 0, fontSize: "1.6rem" }}>{layout?.nome ?? codigo}</h1>
      {layout && dados ? (
        <>
          <LayoutChip
            tamanho="md"
            nome={layout.nome}
            codigo={layout.codigo}
            versao={layout.versao}
            situacao={layout.situacao}
            padrao={layout.padrao}
          />
          <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>{layout.descricao}</p>
          <p style={{ margin: 0, fontSize: 13 }}>
            {dados.atribuivel.ok
              ? "Atribuível: sim"
              : `Atribuível: não — ${dados.atribuivel.motivo ?? "motivo não informado"}`}
          </p>
        </>
      ) : carregando ? (
        <Skeleton linhas={3} altura={16} largura="60%" />
      ) : null}
    </header>
  );
}

/** Os cartões que toda ficha tem, com esqueleto no lugar do conteúdo. */
const SECOES_FIXAS = [
  "Última prova",
  "Cenários de forma",
  "Limitações",
  "Campos derivados",
  "Unidades que usam",
  "Documentação",
  "Exemplo",
];

function Esqueleto() {
  return (
    <>
      {SECOES_FIXAS.map((titulo) => (
        <Secao key={titulo} titulo={titulo} carregando>
          <Skeleton linhas={3} altura={16} />
        </Secao>
      ))}
    </>
  );
}

// ============================================================ pendências

function PendenciasCard({ pendencias }: { pendencias: FichaLayout["pendencias"] }) {
  return (
    <Secao titulo="Pendências para publicação">
      {pendencias.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13, color: "var(--text-dim)" }}>Nenhuma pendência registrada.</p>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, display: "grid", gap: 4 }}>
          {pendencias.map((p) => (
            <li key={p.descricao}>
              {p.descricao} <span style={{ color: "var(--text-dim)" }}>· desde {fmtDataISO(p.desde)}</span>
            </li>
          ))}
        </ul>
      )}
    </Secao>
  );
}

// ============================================================ prova

/**
 * O que o catálogo declara × o que este ambiente registrou. `ok` e
 * `nao_aplicavel` não têm frase: silêncio é o estado bom.
 */
const ALERTA_VERIFICACAO: Partial<Record<FichaLayout["provaHomologVerificacao"], string>> = {
  nao_encontrada: "A prova declarada no catálogo não foi encontrada neste ambiente.",
  nao_verde: "A prova declarada no catálogo não está verde.",
};

function PilulaProva({ resumo }: { resumo: ResumoProva | null }) {
  if (!resumo) return <Pill variant="expirado">nunca provado</Pill>;
  if (resumo.resultado === "verde")
    return <Pill variant="aceita">{`verde · ${fmtData(resumo.terminadoEm)}`}</Pill>;
  // O passo da falha vai no `title`: quem passa o mouse vê onde parou sem
  // descer até a tabela.
  return (
    <span title={resumo.passoDaFalha ?? ""}>
      <Pill variant="rejeitada">{`falha · ${fmtData(resumo.terminadoEm)}`}</Pill>
    </span>
  );
}

const COR_FLUXO: Record<LayoutProva["fluxos"][number]["resultado"], string | undefined> = {
  verde: undefined,
  falha: "var(--danger-500)",
  pulado: "var(--text-dim)",
};

/** "F1 verde · F2 falha · F3 pulado" — compacto, com o detalhe de cada fluxo no `title`. */
function Fluxos({ fluxos }: { fluxos: LayoutProva["fluxos"] }) {
  if (fluxos.length === 0) return <Travessao />;
  return (
    <span style={{ fontSize: 12 }}>
      {fluxos.map((f, i) => (
        <span key={f.codigo}>
          {i > 0 ? " · " : ""}
          <span title={f.detalhe ?? f.nome} style={{ color: COR_FLUXO[f.resultado] }}>
            {f.codigo} {f.resultado}
          </span>
        </span>
      ))}
    </span>
  );
}

const COLUNAS_PROVA: Column<LayoutProva>[] = [
  { key: "quando", header: "Quando", render: (p) => fmtDataHora(p.terminadoEm) },
  { key: "ambiente", header: "Ambiente", render: (p) => ROTULO_AMBIENTE[p.ambiente] ?? p.ambiente },
  {
    key: "resultado",
    header: "Resultado",
    render: (p) => <Pill variant={p.resultado === "verde" ? "aceita" : "rejeitada"}>{p.resultado}</Pill>,
  },
  { key: "versao", header: "Versão", render: (p) => p.layoutVersao },
  { key: "fluxos", header: "Fluxos", wrap: true, render: (p) => <Fluxos fluxos={p.fluxos} /> },
  { key: "detalhe", header: "Detalhe", wrap: true, render: (p) => p.passoDaFalha ?? <Travessao /> },
];

function ProvaCard({ ficha }: { ficha: FichaLayout }) {
  const { layout, provaHomolog, provaHomologVerificacao, provas } = ficha;
  const alerta = ALERTA_VERIFICACAO[provaHomologVerificacao];
  return (
    <Secao titulo="Última prova">
      <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 12 }}>
        {AMBIENTES.map((a) => (
          <div
            key={a.chave}
            data-ambiente={a.chave}
            style={{ display: "grid", gap: 4, justifyItems: "start" }}
          >
            <span style={{ fontSize: 12, color: "var(--text-dim)" }}>{a.rotulo}</span>
            <PilulaProva resumo={layout.ultimaProva[a.chave]} />
          </div>
        ))}
      </div>
      <p style={{ margin: "0 0 8px", fontSize: 13 }}>
        {provaHomolog
          ? `Declarada no catálogo: ${fmtDataISO(provaHomolog.data)} · ${provaHomolog.referencia}`
          : "Nenhuma prova em homolog declarada no catálogo."}
      </p>
      {alerta ? (
        <p role="alert" style={{ margin: "0 0 12px", fontSize: 13, color: "var(--danger-500)" }}>
          {alerta}
        </p>
      ) : null}
      <DataTable
        caption="Execuções da prova"
        columns={COLUNAS_PROVA}
        rows={provas}
        rowKey={(p) => p.id}
        emptyState={
          <EstadoVazio
            titulo="Nunca provado neste ambiente"
            orientacao="A prova de ponta a ponta roda por pedido do dono do repositório; o resultado aparece aqui."
          />
        }
      />
    </Secao>
  );
}

// ============================================================ cenários

interface LinhaCenario {
  dimensao: string;
  rotulo: string;
  aceita: string[];
  naoAceito: { valor: string; motivo: string }[];
}

/**
 * Uma linha por dimensão, na ordem da matriz quando ela já chegou. As chaves
 * que o layout declara e a matriz não conhece entram no fim, sem rótulo —
 * melhor mostrar a chave crua do que sumir com uma declaração.
 */
function montarLinhas(cenarios: FichaLayout["layout"]["cenarios"], matriz?: MatrizCenarios): LinhaCenario[] {
  const declaradas = cenarios as Record<string, { valor: string; rotulo: string }[] | undefined>;
  const daMatriz = matriz?.dimensoes ?? [];
  const conhecidas = new Set<string>(daMatriz.map((d) => d.dimensao));
  const restantes = Object.keys(declaradas).filter((k) => !conhecidas.has(k));
  return [
    ...daMatriz.map((d) => ({
      dimensao: d.dimensao,
      rotulo: d.rotulo,
      aceita: (declaradas[d.dimensao] ?? []).map((v) => v.rotulo),
      naoAceito: d.naoSuportados.map((n) => ({ valor: n.valor, motivo: n.motivo })),
    })),
    ...restantes.map((k) => ({
      dimensao: k,
      rotulo: k,
      aceita: (declaradas[k] ?? []).map((v) => v.rotulo),
      naoAceito: [],
    })),
  ];
}

const COLUNAS_CENARIO: Column<LinhaCenario>[] = [
  { key: "dimensao", header: "Dimensão", render: (l) => <b>{l.rotulo}</b> },
  {
    key: "aceita",
    header: "O que o layout aceita",
    wrap: true,
    render: (l) => (l.aceita.length === 0 ? <Travessao /> : l.aceita.join(" · ")),
  },
  {
    key: "naoAceito",
    header: "Não aceito",
    wrap: true,
    // Em cinza de propósito: é o que a matriz conhece e recusa com motivo —
    // informação para quem compara com o arquivo do ente, não um erro da ficha.
    render: (l) =>
      l.naoAceito.length === 0 ? (
        <Travessao />
      ) : (
        <div style={{ display: "grid", gap: 2, fontSize: 12, color: "var(--text-dim)" }}>
          {l.naoAceito.map((n) => (
            <div key={n.valor}>
              <code style={{ fontFamily: "var(--font-mono)" }}>{n.valor}</code>: {n.motivo}
            </div>
          ))}
        </div>
      ),
  },
];

function CenariosCard({
  cenarios,
  matriz,
}: {
  cenarios: FichaLayout["layout"]["cenarios"];
  matriz: UseQueryResult<MatrizCenarios>;
}) {
  const linhas = useMemo(() => montarLinhas(cenarios, matriz.data), [cenarios, matriz.data]);
  return (
    <Secao titulo="Cenários de forma">
      {matriz.isError ? (
        <div style={{ marginBottom: 10 }}>
          <EstadoErro
            mensagem="A matriz de cenários não carregou; a coluna “Não aceito” fica sem os motivos."
            onTentarDeNovo={() => void matriz.refetch()}
          />
        </div>
      ) : null}
      <DataTable
        caption="Cenários de forma declarados"
        columns={COLUNAS_CENARIO}
        rows={linhas}
        rowKey={(l) => l.dimensao}
        loading={matriz.isLoading}
        emptyState={
          <EstadoVazio
            titulo="Nenhum cenário declarado"
            orientacao="Todo layout do catálogo declara um valor por dimensão; sem declaração, a cobertura não pode ser calculada."
          />
        }
      />
      <p style={{ margin: "10px 0 0", fontSize: 13, color: "var(--text-muted)" }}>
        Compatível é sobre forma — o que a matriz mede. A semântica das colunas é do roteiro de inclusão.
      </p>
    </Secao>
  );
}

// ============================================================ limitações e derivados

function LimitacoesCard({ limitacoes }: { limitacoes: string[] }) {
  return (
    <Secao titulo="Limitações">
      {limitacoes.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13, color: "var(--text-dim)" }}>
          Nenhuma: carrega tudo que o padrão Atlas carrega.
        </p>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, display: "grid", gap: 4 }}>
          {limitacoes.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      )}
    </Secao>
  );
}

type CampoDerivado = FichaLayout["layout"]["camposDerivados"][number];

const COLUNAS_DERIVADO: Column<CampoDerivado>[] = [
  { key: "campo", header: "Campo", mono: true },
  { key: "regra", header: "Regra", wrap: true },
];

function CamposDerivadosCard({ campos }: { campos: CampoDerivado[] }) {
  return (
    <Secao titulo="Campos derivados">
      {campos.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13, color: "var(--text-dim)" }}>Nenhum campo é derivado.</p>
      ) : (
        <DataTable
          caption="Campos derivados"
          columns={COLUNAS_DERIVADO}
          rows={campos}
          rowKey={(c) => c.campo}
        />
      )}
    </Secao>
  );
}

// ============================================================ unidades

const COLUNAS_UNIDADE: Column<UnidadeQueUsa>[] = [
  {
    key: "nome",
    header: "Unidade",
    wrap: true,
    render: (u) => (
      <Link to={`/averbadora/layouts/unidade/${u.id}`} style={{ color: "var(--accent)" }}>
        {u.nome}
      </Link>
    ),
  },
  { key: "sentido", header: "Sentido", render: (u) => (u.sentido === "entrada" ? "Retorno" : "Movimento") },
  { key: "desde", header: "Desde", render: (u) => fmtDesde(u.desde) },
  { key: "agendada", header: "Agendada", render: (u) => (u.agendada ? "sim" : <Travessao />) },
  {
    key: "deProva",
    header: "De prova",
    render: (u) => (u.deProva ? <Pill variant="expirado">unidade de prova</Pill> : <Travessao />),
  },
];

function UnidadesCard({ unidades }: { unidades: UnidadeQueUsa[] }) {
  return (
    <Secao titulo="Unidades que usam">
      <DataTable
        caption="Unidades que usam este layout"
        columns={COLUNAS_UNIDADE}
        rows={unidades}
        rowKey={(u) => `${u.id}-${u.sentido}`}
        emptyState={
          <EstadoVazio
            titulo="Nenhuma unidade usa este layout"
            orientacao="Nenhuma unidade tem este layout atribuído, em vigor ou agendado. A atribuição é feita no painel da unidade."
          />
        }
      />
    </Secao>
  );
}

// ============================================================ documentação

/**
 * O documento técnico, renderizado aqui e também aberto em aba. `alert` no
 * erro dos botões é o padrão da 006: caminho de exceção (rota fora do ar,
 * sessão expirada), não um fluxo — um lugar na tela custaria mais do que vale.
 */
function DocumentacaoCard({ codigo }: { codigo: string }) {
  const doc = useQuery({
    queryKey: ["admin", "layouts", "documento", codigo, "md"],
    queryFn: async () => (await atlas.admin.layouts.documentoBlob(codigo, "md")).text(),
  });

  const abrir = async (fmt: "pdf" | "md") => {
    const erro = await abrirDocumentoDeLayout(() => atlas.admin.layouts.documentoBlob(codigo, fmt));
    if (erro) alert(erro);
  };

  return (
    <Secao
      titulo="Documentação"
      acoes={
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button size="sm" variant="ghost" onClick={() => void abrir("pdf")}>
            Abrir PDF
          </Button>
          <Button size="sm" variant="ghost" onClick={() => void abrir("md")}>
            Baixar Markdown
          </Button>
        </div>
      }
    >
      {doc.isLoading ? (
        <Skeleton linhas={8} altura={14} />
      ) : doc.isError ? (
        <EstadoErro
          mensagem={`Não foi possível carregar o documento: ${mensagemDoErro(doc.error)}`}
          onTentarDeNovo={() => void doc.refetch()}
        />
      ) : (
        <Markdown>{doc.data ?? ""}</Markdown>
      )}
    </Secao>
  );
}

// ============================================================ exemplo

function ExemploCard({ codigo, exemplo }: { codigo: string; exemplo: FichaLayout["exemplo"] }) {
  const baixar = useMutation({
    mutationFn: async () =>
      baixarBlob(await atlas.admin.layouts.exemploBlob(codigo), `${codigo}-exemplo.zip`),
  });
  return (
    <Secao
      titulo="Exemplo"
      acoes={
        <Button size="sm" variant="ghost" disabled={baixar.isPending} onClick={() => baixar.mutate()}>
          {baixar.isPending ? "Baixando…" : "Baixar pacote de exemplo"}
        </Button>
      }
    >
      <p style={{ margin: "0 0 8px", fontSize: 13, color: "var(--text-muted)" }}>{exemplo.descricao}</p>
      {baixar.isError ? (
        <p role="alert" style={{ margin: "0 0 8px", fontSize: 13, color: "var(--danger-500)" }}>
          Não foi possível baixar o exemplo: {mensagemDoErro(baixar.error)}
        </p>
      ) : null}
      {exemplo.arquivos.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13, color: "var(--text-dim)" }}>
          O pacote de exemplo não lista arquivos.
        </p>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, display: "grid", gap: 4 }}>
          {exemplo.arquivos.map((a) => (
            <li key={a}>
              <code style={{ fontFamily: "var(--font-mono)" }}>{a}</code>
            </li>
          ))}
        </ul>
      )}
    </Secao>
  );
}
